import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { statsApi, missionsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  Plane, 
  Map, 
  Plus, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Activity,
  FileText,
  Download
} from 'lucide-react';
import { LocalRegulationCard } from '../components/dashboard/LocalRegulationCard';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentMissions, setRecentMissions] = useState([]);
  const [pdfDownloads, setPdfDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, missionsRes] = await Promise.all([
        statsApi.getDashboard(),
        missionsApi.getAll()
      ]);
      setStats(statsRes.data || { total_missions: 0, total_drones: 0, risk_distribution: { LOW: 0, MODERATE: 0, HIGH: 0 } });
      setRecentMissions(missionsRes.data?.slice(0, 5) || []);
      setPdfDownloads(statsRes.data?.recent_pdf_downloads || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setStats({ total_missions: 0, total_drones: 0, risk_distribution: { LOW: 0, MODERATE: 0, HIGH: 0 }, missions_remaining: null });
      setRecentMissions([]);
      setPdfDownloads([]);
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user?.subscription_status === 'solo' || user?.subscription_status === 'partner';

  const getRiskBadge = (level) => {
    const config = {
      LOW: { class: 'badge-safe', label: t('dashboard.risk.low'), icon: CheckCircle },
      MODERATE: { class: 'badge-warning', label: t('dashboard.risk.moderate'), icon: AlertTriangle },
      HIGH: { class: 'badge-danger', label: t('dashboard.risk.high'), icon: AlertTriangle },
    };
    const { class: className, label, icon: Icon } = config[level] || config.LOW;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title={t('dashboard.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={t('dashboard.title')}
      actions={
        <Link to="/missions/new">
          <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="new-mission-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.newMission')}
          </Button>
        </Link>
      }
    >
      {/* Welcome Message */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 font-[Barlow]">
          {t('dashboard.welcome')}, {user?.name?.split(' ')[0]} !
        </h2>
        <p className="text-slate-600 mt-1">
          {t('dashboard.welcomeMessage')}
        </p>
      </div>

      {/* Local regulation shortcut — surfaces the user's country guide */}
      <LocalRegulationCard country={user?.country} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card" data-testid="stat-missions">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Map className="w-5 h-5 text-sky-600" />
            </div>
            {stats?.missions_limit && (
              <span className="text-xs text-slate-500 font-mono">
                {stats.total_missions}/{stats.missions_limit}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 mb-1">{t('dashboard.stats.missions')}</p>
          <p className="text-2xl font-bold text-slate-900 font-[Barlow]">{stats?.total_missions || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-drones">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Plane className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-1">{t('dashboard.stats.registeredDrones')}</p>
          <p className="text-2xl font-bold text-slate-900 font-[Barlow]">{stats?.total_drones || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-risk-low">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-1">{t('dashboard.stats.lowRisk')}</p>
          <p className="text-2xl font-bold text-slate-900 font-[Barlow]">{stats?.risk_distribution?.LOW || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-risk-high">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-1">{t('dashboard.stats.highRisk')}</p>
          <p className="text-2xl font-bold text-slate-900 font-[Barlow]">{stats?.risk_distribution?.HIGH || 0}</p>
        </div>
      </div>

      {/* Subscription Banner for Free Users */}
      {!isPremium && stats?.missions_remaining !== undefined && stats?.missions_remaining !== null && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg font-[Barlow]">
                {stats.missions_remaining > 0 
                  ? t('dashboard.subscription.remainingMissions', { count: stats.missions_remaining })
                  : t('dashboard.subscription.noMissionsLeft')}
              </h3>
              <p className="text-slate-300 text-sm mt-1">
                {t('dashboard.subscription.upgradeMessage')}
              </p>
            </div>
            <Link to="/subscription">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white whitespace-nowrap" data-testid="upgrade-btn">
                {t('dashboard.subscription.upgradeButton')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Quick Actions & Recent Missions */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-slate-900 font-[Barlow] mb-4">{t('dashboard.quickActions.title')}</h3>
          <div className="space-y-3">
            <Link to="/missions/new" className="block">
              <div className="p-4 bg-white border border-slate-200 rounded-lg hover:border-sky-300 hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Plus className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{t('dashboard.quickActions.createMission')}</p>
                  <p className="text-sm text-slate-500">{t('dashboard.quickActions.createMissionDesc')}</p>
                </div>
              </div>
            </Link>
            <Link to="/drones" className="block">
              <div className="p-4 bg-white border border-slate-200 rounded-lg hover:border-sky-300 hover:shadow-md transition-all duration-200 flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Plane className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{t('dashboard.quickActions.manageDrones')}</p>
                  <p className="text-sm text-slate-500">{t('dashboard.quickActions.manageDronesDesc')}</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Missions */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 font-[Barlow]">{t('dashboard.recentMissions.title')}</h3>
            <Link to="/missions" className="text-sm text-sky-600 hover:text-sky-700 font-medium">
              {t('common.viewAll')}
            </Link>
          </div>
          
          {recentMissions.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">{t('dashboard.recentMissions.empty')}</p>
              <Link to="/missions/new">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="create-first-mission-btn">
                  {t('dashboard.recentMissions.createFirst')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="divide-y divide-slate-200">
                {recentMissions.map((mission) => (
                  <Link 
                    key={mission.id} 
                    to={`/missions/${mission.id}`}
                    className="block p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{mission.mission_type}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {new Date(mission.mission_date).toLocaleDateString()} - {mission.mission_time}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRiskBadge(mission.regulation_result?.risk_level)}
                        <span className="text-sm font-mono text-slate-600">
                          {t('common.score')}: {mission.risk_score}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PDF Download History */}
      {pdfDownloads.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 font-[Barlow] flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-500" />
              {t('dashboard.pdfHistory.title')}
            </h3>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="divide-y divide-slate-100">
              {pdfDownloads.slice(0, 5).map((download) => (
                <Link 
                  key={download.id} 
                  to={`/missions/${download.mission_id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-sky-100 rounded-lg flex items-center justify-center">
                      <Download className="w-4 h-4 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 capitalize">{download.mission_type}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(download.downloaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
